import React from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import {
  Paper,
  Grid,
  Box,
  Typography,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  IconButton,
  Button,
  Divider,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import AdverseEventRelevanceItem from './AdverseEventRelevanceItem';

// 반복 섹션 컨테이너(Grid container)에 공통 적용
const repeatBoxSx = {
  mb: 1,
  p: 2, // p:2로 충분한 내부 여백 확보
  pr: 6, // 우측 아이콘 공간 확보(48px 이상 권장)
  border: '1px solid',
  borderRadius: 1,
  position: 'relative',
  backgroundColor: 'background.paper',
};

// 우측 상단 X 아이콘 버튼 스타일
const closeBtnSx = {
  position: 'absolute',
  top: 6,
  right: 6,
  width: 36,
  height: 36,
  color: 'error.main',
  zIndex: 2,
  '& svg': { fontSize: 20 },
  '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)', color: 'error.dark' },
};

// 첫 열(form field) 상단 여백 살짝 추가: 아이콘과 안 겹치게
const firstColSx = { mt: { xs: 4, md: 0 } };

export default function DrugInfoCard({ index, removeCard }) {
  const { control, watch, setValue } = useFormContext();
  const base = `drugInfos.${index}`;

  // 병용요법 감시
  const therapyValue = watch(`${base}.concomitantTherapy`);

  // 1. 첩약 처방 정보 - 반복 블록들
  const {
    fields: compFields,
    append: compAppend,
    remove: compRemove,
  } = useFieldArray({ control, name: `${base}.herbalPrescription.herbCompositions` });

  const {
    fields: itemFields,
    append: itemAppend,
    remove: itemRemove,
  } = useFieldArray({ control, name: `${base}.herbalPrescription.herbalItemInfos` });

  // 2-1. 투여 정보 (복수)
  const {
    fields: adminFields,
    append: adminAppend,
    remove: adminRemove,
  } = useFieldArray({ control, name: `${base}.administrationHistory` });

  // 5. 병용약물 정보 (복수)
  const {
    fields: comboFields,
    append: comboAppend,
    remove: comboRemove,
  } = useFieldArray({ control, name: `${base}.concomitantDrugs` });

  // 7. 관련성 (복수)
  const {
    fields: relFields,
    append: relAppend,
    remove: relRemove,
  } = useFieldArray({ control, name: `${base}.adverseEventRelevance` });

  React.useEffect(() => {
    if (therapyValue !== 'other') {
      setValue(`${base}.concomitantTherapyOther`, '');
    }
  }, [therapyValue, base, setValue]);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2}>
        {/* 카드 헤더 + 삭제 */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1">#{index + 1} 의약품 정보</Typography>
            <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={removeCard}>
              의약품 정보 삭제
            </Button>
          </Box>
        </Grid>
        {/* 1. 첩약 처방 정보 */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            1. 첩약 처방 정보
          </Typography>
        </Grid>
        {/* 의심되는 약물 이름(처방명) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name={`${base}.herbalPrescription.drugName`}
            control={control}
            defaultValue=""
            render={({ field }) => <TextField {...field} label="의심되는 약물 이름 (처방명)" fullWidth />}
          />
        </Grid>
        {/* 첩당 구성 한약재 및 용량 */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: { xs: 1, md: 0 } }}>
            첩당 구성 한약재 및 용량
          </Typography>
        </Grid>
        {compFields.map((row, ci) => {
          const cn = `${base}.herbalPrescription.herbCompositions.${ci}`;
          return (
            <Grid
              size={{ xs: 6 }}
              container
              spacing={1}
              key={row.id}
              sx={{ ...repeatBoxSx, border: '1px solid #7CB8FF' }}
            >
              <IconButton onClick={() => compRemove(ci)} aria-label="한약재 구성 삭제" sx={closeBtnSx}>
                <CloseIcon />
              </IconButton>

              <Grid size={{ xs: 12, md: 6 }} sx={firstColSx}>
                <Controller
                  name={`${cn}.herbName`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => <TextField {...field} label="한약재명" fullWidth />}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name={`${cn}.amountGram`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="용량(g)"
                      fullWidth
                      type="number"
                      inputProps={{ min: 0 }}
                      value={field.value ?? ''}
                    />
                  )}
                />
              </Grid>
            </Grid>
          );
        })}
        <Grid size={{ xs: 12 }}>
          <Button variant="outlined" size="small" onClick={() => compAppend({ herbName: '', amountGram: '' })}>
            구성 한약재 추가
          </Button>
        </Grid>
        {/* 총 첩수 / 총 팩수 */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name={`${base}.herbalPrescription.totalDoseCount`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField {...field} label="총 첩수 (처방전 기준)" fullWidth value={field.value ?? ''} />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name={`${base}.herbalPrescription.totalPackCount`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField {...field} label="총 팩수 (처방전 기준)" fullWidth value={field.value ?? ''} />
            )}
          />
        </Grid>
        {/* 1일 복용팩수 / 1팩 용량(ml) */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name={`${base}.herbalPrescription.packsPerDay`}
            control={control}
            defaultValue=""
            render={({ field }) => <TextField {...field} label="1일 복용팩수" fullWidth value={field.value ?? ''} />}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name={`${base}.herbalPrescription.volumePerPack`}
            control={control}
            defaultValue=""
            render={({ field }) => <TextField {...field} label="1팩 용량(ml)" fullWidth value={field.value ?? ''} />}
          />
        </Grid>
        {/* 원내탕전/원외탕전 여부 */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            원내탕전/원외탕전 여부
          </Typography>
          <Controller
            name={`${base}.herbalPrescription.decoctionLocation`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <RadioGroup row {...field}>
                <FormControlLabel value="inHospital" control={<Radio />} label="원내탕전" />
                <FormControlLabel value="external" control={<Radio />} label="원외탕전" />
              </RadioGroup>
            )}
          />
        </Grid>
        {/* 한약재 품목 정보 */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            한약재 품목 정보
          </Typography>
        </Grid>
        {itemFields.map((row, ii) => {
          const iname = `${base}.herbalPrescription.herbalItemInfos.${ii}`;
          return (
            <Grid container spacing={1} key={row.id} sx={{ ...repeatBoxSx, border: '1px solid #7CB8FF' }}>
              <IconButton onClick={() => itemRemove(ii)} aria-label="품목 정보 삭제" sx={closeBtnSx}>
                <CloseIcon />
              </IconButton>

              <Grid size={{ xs: 12, md: 4 }} sx={firstColSx}>
                <Controller
                  name={`${iname}.itemHerbName`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => <TextField {...field} label="한약재명" fullWidth />}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name={`${iname}.manufacturer`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => <TextField {...field} label="업체명" fullWidth />}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name={`${iname}.origin`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => <TextField {...field} label="산지" fullWidth />}
                />
              </Grid>
            </Grid>
          );
        })}
        <Grid size={{ xs: 12 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => itemAppend({ itemHerbName: '', manufacturer: '', origin: '' })}
          >
            품목 정보 추가
          </Button>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        {/* 2-1. 투여 정보 (복수 작성 가능) */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            2-1. 투여 정보 (복수 작성 가능)
          </Typography>
        </Grid>
        {adminFields.map((row, ai) => {
          const an = `${base}.administrationHistory.${ai}`;

          // 라디오 값 watch
          const routeValue = watch(`${an}.route`);
          const dosageFormValue = watch(`${an}.dosageForm`);

          return (
            <Grid container spacing={1} key={row.id} sx={{ ...repeatBoxSx, border: '1px solid #7CB8FF' }}>
              <IconButton onClick={() => adminRemove(ai)} aria-label="투여정보 삭제" sx={closeBtnSx}>
                <CloseIcon />
              </IconButton>

              <Grid size={{ xs: 12, md: 'grow' }} sx={firstColSx}>
                <Controller
                  name={`${an}.indication`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => <TextField {...field} label="투여 적응증" fullWidth />}
                />
              </Grid>

              {/* 시작일 / 종료일 */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                  name={`${an}.startDate`}
                  control={control}
                  defaultValue=""
                  render={({ field: { value, onChange, ...rest } }) => (
                    <TextField
                      label="투여 시작일"
                      placeholder="YYYYMMDD"
                      fullWidth
                      value={value ?? null}
                      onChange={(e) => {
                        const next = e.target.value.replace(/\D/g, '').slice(0, 8);
                        onChange(next);
                      }}
                      inputMode="numeric"
                      slotProps={{ htmlInput: { maxLength: 8 } }}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData('text');
                        // 숫자 이외 포함 또는 8자리 초과는 붙여넣기 차단
                        if (/\D/.test(pasted) || pasted.replace(/\D/g, '').length > 8) {
                          e.preventDefault();
                        }
                      }}
                      {...rest}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                  name={`${an}.endDate`}
                  control={control}
                  defaultValue=""
                  render={({ field: { value, onChange, ...rest } }) => (
                    <TextField
                      label="투여 종료일"
                      placeholder="YYYYMMDD"
                      fullWidth
                      value={value ?? null}
                      onChange={(e) => {
                        const next = e.target.value.replace(/\D/g, '').slice(0, 8);
                        onChange(next);
                      }}
                      inputMode="numeric"
                      slotProps={{ htmlInput: { maxLength: 8 } }}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData('text');
                        // 숫자 이외 포함 또는 8자리 초과는 붙여넣기 차단
                        if (/\D/.test(pasted) || pasted.replace(/\D/g, '').length > 8) {
                          e.preventDefault();
                        }
                      }}
                      {...rest}
                    />
                  )}
                />
              </Grid>

              {/* 최초 발생까지의 실제 복용량(팩수) */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                  name={`${an}.totalPacksUntilOnset`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="최초 발생까지의 실제 복용량(팩수)"
                      type="number"
                      fullWidth
                      value={field.value ?? ''}
                    />
                  )}
                />
              </Grid>

              {/* 투여 경로: 경구/외용/기타(+기타 입력) */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  투여 경로
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2 }}>
                  <Controller
                    name={`${an}.route`}
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <RadioGroup row {...field}>
                        <FormControlLabel value="oral" control={<Radio />} label="경구복용" />
                        <FormControlLabel value="topical" control={<Radio />} label="외용" />
                        <FormControlLabel value="other" control={<Radio />} label="기타" />
                      </RadioGroup>
                    )}
                  />

                  {routeValue === 'other' ? (
                    <Controller
                      name={`${an}.routeOther`}
                      control={control}
                      defaultValue=""
                      render={({ field }) => <TextField {...field} label="기타(투여 경로)" sx={{ width: 600 }} />}
                    />
                  ) : (
                    <Box sx={{ width: 600 }} />
                  )}
                </Box>
              </Grid>

              {/* 투여 간격*/}
              <Grid container size={{ xs: 6 }}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    투여 간격
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 'auto' }}>
                  <Controller
                    name={`${an}.dosageIntervalUnit`}
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <RadioGroup row {...field}>
                        <FormControlLabel value="dose" control={<Radio />} label="첩" />
                        <FormControlLabel value="pack" control={<Radio />} label="팩" />
                      </RadioGroup>
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    1일
                  </Typography>
                  <Controller
                    name={`${an}.dosageIntervalCount`}
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField {...field} type="number" label="회" sx={{ width: 120 }} value={field.value ?? ''} />
                    )}
                  />
                </Grid>

                {/* 투여 기간 */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name={`${an}.dosagePeriod`}
                    control={control}
                    defaultValue=""
                    render={({ field }) => <TextField {...field} label="투여 기간" placeholder="예: 14일" fullWidth />}
                  />
                </Grid>
              </Grid>

              {/* 제형: dosageForm / dosageFormOther */}
              <Grid container size={{ xs: 6 }}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    제형 (dosageForm / dosageFormOther)
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                  <Controller
                    name={`${an}.dosageForm`}
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <RadioGroup row {...field}>
                        <FormControlLabel value="decoction" control={<Radio />} label="탕" />
                        <FormControlLabel value="pill" control={<Radio />} label="환" />
                        <FormControlLabel value="powder" control={<Radio />} label="산" />
                        <FormControlLabel value="tablet" control={<Radio />} label="정" />
                        <FormControlLabel value="other" control={<Radio />} label="기타" />
                      </RadioGroup>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  {dosageFormValue === 'other' ? (
                    <Controller
                      name={`${an}.dosageFormOther`}
                      control={control}
                      defaultValue=""
                      render={({ field }) => <TextField {...field} label="기타(제형)" fullWidth />}
                    />
                  ) : null}
                </Grid>
              </Grid>
            </Grid>
          );
        })}
        <Grid size={{ xs: 12 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              adminAppend({
                indication: '',
                startDate: '',
                endDate: '',
                totalPacksUntilOnset: '',
                route: '',
                routeOther: '',
                dosageIntervalUnit: '',
                dosageIntervalCount: '',
                dosagePeriod: '',
                dosageForm: '',
                dosageFormOther: '',
              })
            }
          >
            투여 정보 추가
          </Button>
        </Grid>
        {/* 2-2. 태아/신생아/영아 투여 정보 */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            2-2. 투여 정보(환자가 태아/신생아/영아인 경우)
          </Typography>
        </Grid>
        {/* 부모의 투여경로 (자유 입력) */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Controller
            name={`${base}.drugAdministrationFetus.routeParent`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField {...field} label="부모의 투여경로" placeholder="예: 경구복용, 외용 등 자유기재" fullWidth />
            )}
          />
        </Grid>
        {/* 최초 노출 시점의 임신기간 - 숫자 입력 */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name={`${base}.drugAdministrationFetus.firstExposureGestation.value`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="최초 노출 시점의 임신기간"
                type="number"
                inputMode="numeric"
                fullWidth
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
        </Grid>
        {/* 임신기간 단위 라디오: 일/주/월/임신분기 */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            임신기간 단위
          </Typography>
          <Controller
            name={`${base}.drugAdministrationFetus.firstExposureGestation.unit`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <RadioGroup row {...field}>
                <FormControlLabel value="day" control={<Radio />} label="일" />
                <FormControlLabel value="week" control={<Radio />} label="주" />
                <FormControlLabel value="month" control={<Radio />} label="월" />
                <FormControlLabel value="trimester" control={<Radio />} label="임신분기" />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        {/* 3. 이상반응 치료(조치) */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            3. 이상반응에 대한 치료(조치)
          </Typography>
          <Controller
            name={`${base}.adverseEventTreatment`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <RadioGroup row {...field}>
                <FormControlLabel value="discontinued" control={<Radio />} label="투여 중지" />
                <FormControlLabel value="doseDecreased" control={<Radio />} label="투여량 감소" />
                <FormControlLabel value="doseIncreased" control={<Radio />} label="투여량 증가" />
                <FormControlLabel value="doseMaintained" control={<Radio />} label="투여량 유지" />
                <FormControlLabel value="unknown" control={<Radio />} label="모름" />
                <FormControlLabel value="notApplicable" control={<Radio />} label="해당 없음" />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        {/* 4. 병용요법 유무 */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            4. 병용요법 유무
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Controller
              name={`${base}.concomitantTherapy`}
              control={control}
              defaultValue=""
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <FormControlLabel value="westernDrug" control={<Radio />} label="양방치료(약물)" />
                  <FormControlLabel value="westernNonDrug" control={<Radio />} label="양방치료(약물 이외)" />
                  <FormControlLabel value="acupuncture" control={<Radio />} label="호침" />
                  <FormControlLabel value="electroacupuncture" control={<Radio />} label="전침" />
                  <FormControlLabel value="pharmacopuncture" control={<Radio />} label="약침" />
                  <FormControlLabel value="knifeAcupuncture" control={<Radio />} label="도침" />
                  <FormControlLabel value="threadEmbedding" control={<Radio />} label="매선" />
                  <FormControlLabel value="moxibustion" control={<Radio />} label="뜸" />
                  <FormControlLabel value="wetCupping" control={<Radio />} label="습식부항" />
                  <FormControlLabel value="dryCupping" control={<Radio />} label="건식부항" />
                  <FormControlLabel value="chuna" control={<Radio />} label="추나" />
                  <FormControlLabel value="other" control={<Radio />} label="기타" />
                </RadioGroup>
              )}
            />

            {therapyValue === 'other' && (
              <Controller
                name={`${base}.concomitantTherapyOther`}
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} label="기타 (자유기재)" size="small" sx={{ minWidth: 260 }} />
                )}
              />
            )}
          </Box>
        </Grid>
        {/* 5. 병용약물 정보 (복수 작성 가능) */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            5. 병용약물 정보 (복수 작성 가능)
          </Typography>
        </Grid>
        {comboFields.map((row, ci) => {
          const cn = `${base}.concomitantDrugs.${ci}`;
          return (
            <Grid container spacing={1} key={row.id} sx={{ ...repeatBoxSx, border: '1px solid #7CB8FF' }}>
              {/* 섹션 맞춤 삭제 핸들러/라벨 */}
              <IconButton onClick={() => comboRemove(ci)} aria-label="병용약물 삭제" sx={closeBtnSx}>
                <CloseIcon />
              </IconButton>

              {/* 약물명 */}
              <Grid size={{ xs: 12, md: 4 }} sx={{ mt: { xs: 5, md: 0 } }}>
                <Controller
                  name={`${cn}.concomitantDrugName`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => <TextField {...field} label="약물명" fullWidth />}
                />
              </Grid>

              {/* 1일 총 투여량 */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name={`${cn}.dailyTotalDosage`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => <TextField {...field} label="1일 총 투여량" type="number" fullWidth />}
                />
              </Grid>

              {/* 단위 */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name={`${cn}.concomitantDrugUnit`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => <TextField {...field} label="단위 (예: mg, g, ml 등)" fullWidth />}
                />
              </Grid>

              {/* 투여 경로 */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name={`${cn}.administrationRoute`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField {...field} label="투여 경로" placeholder="예: 경구, 외용 등" fullWidth />
                  )}
                />
              </Grid>

              {/* 투여 시작일 */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                  name={`${cn}.administrationStartDate`}
                  control={control}
                  defaultValue=""
                  render={({ field: { value, onChange, ...rest } }) => (
                    <TextField
                      label="투여 시작일"
                      placeholder="YYYYMMDD"
                      fullWidth
                      value={value ?? null}
                      onChange={(e) => {
                        const next = e.target.value.replace(/\D/g, '').slice(0, 8);
                        onChange(next);
                      }}
                      inputMode="numeric"
                      slotProps={{ htmlInput: { maxLength: 8 } }}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData('text');
                        // 숫자 이외 포함 또는 8자리 초과는 붙여넣기 차단
                        if (/\D/.test(pasted) || pasted.replace(/\D/g, '').length > 8) {
                          e.preventDefault();
                        }
                      }}
                      {...rest}
                    />
                  )}
                />
              </Grid>

              {/* 투여 종료일 */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                  name={`${cn}.administrationEndDate`}
                  control={control}
                  defaultValue=""
                  render={({ field: { value, onChange, ...rest } }) => (
                    <TextField
                      label="투여 종료일"
                      placeholder="YYYYMMDD"
                      fullWidth
                      value={value ?? null}
                      onChange={(e) => {
                        const next = e.target.value.replace(/\D/g, '').slice(0, 8);
                        onChange(next);
                      }}
                      inputMode="numeric"
                      slotProps={{ htmlInput: { maxLength: 8 } }}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData('text');
                        // 숫자 이외 포함 또는 8자리 초과는 붙여넣기 차단
                        if (/\D/.test(pasted) || pasted.replace(/\D/g, '').length > 8) {
                          e.preventDefault();
                        }
                      }}
                      {...rest}
                    />
                  )}
                />
              </Grid>

              {/* 현재 지속여부 */}
              <Grid size={{ xs: 12, md: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  현재 지속여부
                </Typography>
                <Controller
                  name={`${cn}.isOngoing`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <RadioGroup row {...field}>
                      <FormControlLabel value="yes" control={<Radio />} label="예" />
                      <FormControlLabel value="no" control={<Radio />} label="아니오" />
                    </RadioGroup>
                  )}
                />
              </Grid>
            </Grid>
          );
        })}
        {/* 항목 추가 버튼 */}
        <Grid size={{ xs: 12 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              comboAppend({
                concomitantDrugName: '',
                dailyTotalDosage: '',
                concomitantDrugUnit: '',
                administrationRoute: '',
                administrationStartDate: '',
                administrationEndDate: '',
                isOngoing: '',
              })
            }
          >
            병용약물 추가
          </Button>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        {/* 6. 과거 병력 및 치료 정보 */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            6. 과거 병력 및 치료 정보
          </Typography>
        </Grid>
        <Grid container spacing={1} sx={{ ...repeatBoxSx, borderColor: 'divider' }}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: { xs: 1, md: 0 } }}>
              과거 병력 (이상사례․약물이상반응 제외)
            </Typography>
          </Grid>

          {/* 과거 병력 및 동반증상(질병/수술처치/기타) */}
          <Grid size={{ xs: 12, md: 12 }}>
            <Controller
              name={`${base}.pastHistory.details`}
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField {...field} label="과거 병력 및 동반증상(질병/수술처치/기타)" fullWidth />
              )}
            />
          </Grid>

          {/* 시작일 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={`${base}.pastHistory.startDate`}
              control={control}
              defaultValue=""
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField
                  label="시작일"
                  placeholder="YYYYMMDD"
                  fullWidth
                  value={value ?? null}
                  onChange={(e) => {
                    const next = e.target.value.replace(/\D/g, '').slice(0, 8);
                    onChange(next);
                  }}
                  inputMode="numeric"
                  slotProps={{ htmlInput: { maxLength: 8 } }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    // 숫자 이외 포함 또는 8자리 초과는 붙여넣기 차단
                    if (/\D/.test(pasted) || pasted.replace(/\D/g, '').length > 8) {
                      e.preventDefault();
                    }
                  }}
                  {...rest}
                />
              )}
            />
          </Grid>

          {/* 지속여부 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              지속여부
            </Typography>
            <Controller
              name={`${base}.pastHistory.isOngoing`}
              control={control}
              defaultValue=""
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <FormControlLabel value="yes" control={<Radio />} label="예" />
                  <FormControlLabel value="no" control={<Radio />} label="아니오" />
                </RadioGroup>
              )}
            />
          </Grid>

          {/* 종료일 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={`${base}.pastHistory.endDate`}
              control={control}
              defaultValue=""
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField
                  label="종료일"
                  placeholder="YYYYMMDD"
                  fullWidth
                  value={value ?? null}
                  onChange={(e) => {
                    const next = e.target.value.replace(/\D/g, '').slice(0, 8);
                    onChange(next);
                  }}
                  inputMode="numeric"
                  slotProps={{ htmlInput: { maxLength: 8 } }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    // 숫자 이외 포함 또는 8자리 초과는 붙여넣기 차단
                    if (/\D/.test(pasted) || pasted.replace(/\D/g, '').length > 8) {
                      e.preventDefault();
                    }
                  }}
                  {...rest}
                />
              )}
            />
          </Grid>

          {/* 비고 */}
          <Grid size={{ xs: 12 }}>
            <Controller
              name={`${base}.pastHistory.note`}
              control={control}
              defaultValue=""
              render={({ field }) => <TextField {...field} label="비고" fullWidth multiline minRows={2} />}
            />
          </Grid>

          {/* 가족력(예 체크박스) + 상세내용 */}
          <Grid
            size={{ xs: 12, md: 2 }}
            sx={{
              display: 'flex',
              flexDirection: 'column', // 세로 배치
              alignItems: 'flex-start', // 좌측 정렬
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0 }}>
              가족력
            </Typography>

            <Controller
              name={`${base}.pastHistory.familyHistory`}
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  sx={{ mt: 0 }} // 제목과 체크박스 사이 간격
                  control={<Checkbox checked={!!field.value} onChange={(_, checked) => field.onChange(checked)} />}
                  label="예"
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 10 }}>
            <Controller
              name={`${base}.pastHistory.familyHistoryDetail`}
              control={control}
              defaultValue=""
              render={({ field }) => <TextField {...field} label="상세내용" fullWidth />}
            />
          </Grid>
        </Grid>
        {/* 과거 의약품등 사용 내역 */}
        <Grid container spacing={1} sx={{ ...repeatBoxSx, borderColor: 'divider' }}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: { xs: 1, md: 0 } }}>
              과거 의약품등 사용 내역
            </Typography>
          </Grid>

          {/* 의약품등의 이름 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={`${base}.pastHistory.previousMedications.name`}
              control={control}
              defaultValue=""
              render={({ field }) => <TextField {...field} label="의약품등의 이름" fullWidth />}
            />
          </Grid>

          {/* 시작일 */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Controller
              name={`${base}.pastHistory.previousMedications.startDate`}
              control={control}
              defaultValue=""
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField
                  label="시작일"
                  placeholder="YYYYMMDD"
                  fullWidth
                  value={value ?? null}
                  onChange={(e) => {
                    const next = e.target.value.replace(/\D/g, '').slice(0, 8);
                    onChange(next);
                  }}
                  inputMode="numeric"
                  slotProps={{ htmlInput: { maxLength: 8 } }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    // 숫자 이외 포함 또는 8자리 초과는 붙여넣기 차단
                    if (/\D/.test(pasted) || pasted.replace(/\D/g, '').length > 8) {
                      e.preventDefault();
                    }
                  }}
                  {...rest}
                />
              )}
            />
          </Grid>

          {/* 종료일 */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Controller
              name={`${base}.pastHistory.previousMedications.endDate`}
              control={control}
              defaultValue=""
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField
                  label="종료일"
                  placeholder="YYYYMMDD"
                  fullWidth
                  value={value ?? null}
                  onChange={(e) => {
                    const next = e.target.value.replace(/\D/g, '').slice(0, 8);
                    onChange(next);
                  }}
                  inputMode="numeric"
                  slotProps={{ htmlInput: { maxLength: 8 } }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    // 숫자 이외 포함 또는 8자리 초과는 붙여넣기 차단
                    if (/\D/.test(pasted) || pasted.replace(/\D/g, '').length > 8) {
                      e.preventDefault();
                    }
                  }}
                  {...rest}
                />
              )}
            />
          </Grid>

          {/* 적응증 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={`${base}.pastHistory.previousMedications.indication`}
              control={control}
              defaultValue=""
              render={({ field }) => <TextField {...field} label="적응증" fullWidth />}
            />
          </Grid>

          {/* 약물이상반응 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name={`${base}.pastHistory.previousMedications.adverseReactionNote`}
              control={control}
              defaultValue=""
              render={({ field }) => <TextField {...field} label="약물이상반응" fullWidth />}
            />
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        {/* 7. 관련성 (복수) */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            7. 이상사례·약물이상반응 관련성 (복수 작성 가능)
          </Typography>
        </Grid>
        {relFields.map((row, ri) => {
          const rn = `${base}.adverseEventRelevance.${ri}`;
          return (
            <AdverseEventRelevanceItem
              key={row.id}
              basePath={rn}
              index={ri}
              onRemove={() => relRemove(ri)}
              repeatBoxsx={{ ...repeatBoxSx, borderColor: 'divider' }}
              closeBtnSx={closeBtnSx}
              firstColSx={firstColSx}
            />
          );
        })}
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            relAppend({
              symptomName: '',
              rechallengeOutcome: '',
              causalAssessmentSets: [
                {
                  whoUmc: { type: 'who-umc', result: '' },
                  other: { type: 'other', methodOther: '', resultOther: '' },
                },
              ],
            })
          }
        >
          이상사례·약물이상반응 관련성 추가
        </Button>
      </Grid>
    </Paper>
  );
}
